/** @file foregroundMask: 1 per pixel that is part of a figure: opaque enough and not the flat background. */
import type { Image, Rgba } from '../types/image';

/** True when the pixel at byte `i` is within `tolerance` (every RGB channel) of one of `colors`. */
function nearAny(data: Uint8ClampedArray, i: number, colors: readonly Rgba[], tolerance: number): boolean {
  for (const c of colors) {
    const d = Math.max(Math.abs((data[i] ?? 0) - c[0]), Math.abs((data[i + 1] ?? 0) - c[1]), Math.abs((data[i + 2] ?? 0) - c[2]));
    if (d <= tolerance) return true;
  }
  return false;
}

/**
 * A `width * height` mask: 1 where alpha is above `alphaThreshold` and, when `background` is given, the colour is
 * farther than `tolerance` from every background colour; 0 elsewhere.
 */
export function foregroundMask(image: Image, alphaThreshold: number, background: readonly Rgba[] | null, tolerance: number): Uint8Array {
  const n = image.width * image.height;
  const mask = new Uint8Array(n);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    if ((image.data[i + 3] ?? 0) <= alphaThreshold) continue;
    if (background !== null && nearAny(image.data, i, background, tolerance)) continue;
    mask[p] = 1;
  }
  return mask;
}
